import AuthReducer, { loginSuccess, logout } from "./AuthReducer";
import { createContext, useEffect, useMemo, useReducer } from "react";

const getToken = (user) =>
  user?.data?.accessToken ?? user?.accessToken ?? user?.token ?? null;

const getInitialUser = () => {
  if (typeof window === "undefined") return null;
  try {
    return JSON.parse(localStorage.getItem("user")) || null;
  } catch {
    return null;
  }
};

const INITIAL_STATE = {
  user: getInitialUser(),
  isFetching: false,
  error: null,
};

export const AuthContext = createContext({
  ...INITIAL_STATE,
  token: null,
  dispatch: () => {},
});

export const AuthContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(AuthReducer, INITIAL_STATE);
  const token = getToken(state.user);

  // persist
  useEffect(() => {
    if (state.user) localStorage.setItem("user", JSON.stringify(state.user));
    else localStorage.removeItem("user");

    if (token) localStorage.setItem("token", token);
    else localStorage.removeItem("token");
  }, [state.user, token]);

  // events from api.js (token refreshed / refresh failed)
  useEffect(() => {
    const onRefreshed = (e) => dispatch(loginSuccess(e.detail));
    const onLogout = () => dispatch(logout());
    window.addEventListener("auth:refreshed", onRefreshed);
    window.addEventListener("auth:logout", onLogout);
    return () => {
      window.removeEventListener("auth:refreshed", onRefreshed);
      window.removeEventListener("auth:logout", onLogout);
    };
  }, []);

  // sync across tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== "user") return;
      if (!e.newValue) dispatch(logout());
      else {
        try {
          dispatch(loginSuccess(JSON.parse(e.newValue)));
        } catch {}
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo(
    () => ({
      user: state.user,
      token,
      isFetching: state.isFetching,
      error: state.error,
      dispatch,
    }),
    [state.user, token, state.isFetching, state.error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
